import {
  ActionName,
  CategoryFieldName,
  CategoryName,
  DisplayModeName,
  DomainName,
  IViewFieldWithDomain,
  resolveButtonedName,
  ResourceId,
  TDatasetName,
  TFormName,
  tg,
} from '@infotech-soe/metaschema';
import conv from 'convert-layout';
import { format as dateFormat } from 'date-fns';
import { IntlMessageFormat } from 'intl-messageformat';

import {
  AVAILABLE_LOCALES,
  DEFAULT_LOCALE,
  LOCALES,
} from '../../constants/intl';
import IIntlStorage from '../../infrastructure/intl/IIntlStorage';
import IntlStorage from '../../infrastructure/intl/IntlStorage';
import { StoredIntlSource } from '../../infrastructure/intl/StoredIntlSource';
import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../infrastructure/schemas/storage/ISchemaRepository';

import IIntlService from './IIntlService';

class IntlServiceSingleton implements IIntlService {
  private readonly schemaRepository: ISchemaRepository;
  private readonly intlStorage: IIntlStorage;
  constructor() {
    this.schemaRepository = new CachedSchemaRepository();
    this.intlStorage = new IntlStorage();
  }

  private async resolveCategoryResource(
    category: CategoryName | null,
    resId: ResourceId,
    altCategory?: CategoryName
  ): Promise<string> {
    let result: string | undefined;
    const { category: categoryName, name: resourceId } =
      resolveButtonedName(resId);
    if (categoryName || category) {
      result = (
        await this.getCategoryMessages(categoryName ?? (category as string))
      )?.[resourceId];
    }
    if (result === undefined && !categoryName) {
      if (altCategory) {
        result = (await this.getCategoryMessages(altCategory))?.[resourceId];
      }
      result =
        result ??
        (await this.intlStorage.getCommon(DEFAULT_LOCALE))?.[resourceId];
    }
    return result ?? resId;
  }

  public async getCategoryFieldTitle(
    category: CategoryName,
    field: CategoryFieldName
  ): Promise<string> {
    return this.resolveCategoryResource(category, field);
  }

  public async getCategoryTitle(category: CategoryName): Promise<string> {
    const addCategory = category.split('\\')[1];
    return this.resolveCategoryResource(null, addCategory ?? category);
  }

  public async getDomainEnumValue(
    domain: DomainName,
    value: string | number
  ): Promise<string> {
    const domainMessages = await this.intlStorage.getDomains(DEFAULT_LOCALE);
    const fullKey = `${domain}.${value}`;

    if (domainMessages[fullKey]) {
      return domainMessages[fullKey];
    }
    return String(value);
  }

  public async getActionName(
    category: CategoryName,
    action: ActionName
  ): Promise<string> {
    return this.resolveCategoryResource(category, action);
  }

  public async getActionArgumentTitle(
    _category: CategoryName,
    _action: ActionName,
    field: CategoryFieldName
  ): Promise<string> {
    return field;
  }

  public async getCategoryMessages(
    category: CategoryName
  ): Promise<Record<string, any>> {
    return this.intlStorage.getCategory(category, DEFAULT_LOCALE);
  }

  public async getCategoryMessage(
    category: CategoryName,
    messageId: string,
    substitutions?: Record<string, any>
  ): Promise<string> {
    const template = await this.resolveCategoryResource(category, messageId);
    if (substitutions && template !== messageId) {
      for (const [name, substitution] of Object.entries(substitutions)) {
        if (tg.hasStringProperty(substitution, 'resId')) {
          substitutions[name] = await this.resolveCategoryResource(
            category,
            substitution.resId
          );
        } else if (
          typeof substitution === 'string' &&
          /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i.exec(
            substitution
          )
        ) {
          substitutions[name] = dateFormat(
            new Date(substitution),
            'dd.MM.yyyy'
          );
        }
      }
    }
    return template
      ? new IntlMessageFormat(template, DEFAULT_LOCALE).format(substitutions)
      : messageId;
  }

  public async getFormFieldTitle(
    category: CategoryName,
    form: TFormName,
    field: CategoryFieldName,
    action?: ActionName,
    resId?: string
  ): Promise<string> {
    if (!resId) {
      const formSchema = await this.schemaRepository.getForm(category, form);
      const domainName = (formSchema.Fields[field] as IViewFieldWithDomain)
        ?.domain;
      const domainMessages = await this.intlStorage.getDomains(DEFAULT_LOCALE);

      if (domainMessages[domainName]) {
        return domainMessages[domainName];
      }
      if (action) {
        return this.getActionArgumentTitle(category, action, field);
      }
      return this.getCategoryFieldTitle(category, field);
    }
    return this.resolveCategoryResource(category, resId);
  }

  public async getApplicationMenuTitle(name: ResourceId): Promise<string> {
    const commonMessages = await this.intlStorage.getCommon(DEFAULT_LOCALE);
    return commonMessages[name] || name;
  }

  public async getDisplayModeTitle(
    category: CategoryName,
    displayMode: DisplayModeName
  ): Promise<string> {
    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      category,
      displayMode
    );
    const resId = displayModeSchema.resId ?? displayMode;
    const title = await this.resolveCategoryResource(category, resId);
    if (title !== resId) {
      return title;
    }
    return this.resolveCategoryResource(category, category);
  }

  public async getDisplayModeDatasetColumnTitle(
    category: CategoryName,
    displayMode: DisplayModeName,
    dataset: TDatasetName,
    column: CategoryFieldName
  ): Promise<string> {
    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      category,
      displayMode
    );
    const ds = displayModeSchema.datasets[dataset];
    if (ds) {
      const colDef = ds.projection.find(i => i.value === column);
      const resId = colDef ? (colDef.resId ?? column) : column;
      const viewSchema = await this.schemaRepository.getView(ds.source);
      return this.resolveCategoryResource(category, resId, viewSchema.Base);
    }
    return column;
  }

  public async getApplicationName(application: string): Promise<string> {
    return this.resolveCategoryResource(null, application);
  }

  public async getDisplayModeDatasetTitle(
    category: CategoryName,
    displayMode: DisplayModeName,
    dataset: TDatasetName
  ): Promise<string> {
    let resId = dataset;
    let altCategory: string | undefined;
    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      category,
      displayMode
    );
    const ds = displayModeSchema.datasets[dataset];
    if (ds) {
      const viewSchema = await this.schemaRepository.getView(ds.source);
      altCategory = viewSchema.Base;
      resId = ds.resId ?? resId;
    }
    return this.resolveCategoryResource(category, resId, altCategory);
  }

  public async getCommon(locale?: string): Promise<StoredIntlSource> {
    return this.intlStorage.getCommon(locale);
  }

  public async getMenuGroupTitle(
    category: CategoryName,
    groupName: string
  ): Promise<string> {
    return this.resolveCategoryResource(category, groupName);
  }

  public getUserInputConversions(
    input: string,
    locale: LOCALES = DEFAULT_LOCALE
  ): string[] {
    const result = [input];
    const layouts: Record<string, layout> = conv as any;
    const currentLayout = layouts[locale];
    if (currentLayout) {
      for (const loc of AVAILABLE_LOCALES) {
        if (loc !== locale) {
          result.push(
            currentLayout.fromEn(
              loc === 'en' || !layouts[loc] ? input : layouts[loc].toEn(input)
            )
          );
        }
      }
    }
    return result;
  }
}

const IntlService = (() => {
  let instance: IIntlService;
  return new Proxy(IntlServiceSingleton, {
    construct(Target: typeof IntlServiceSingleton): IIntlService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default IntlService;
